import axios from 'axios'

// Get CSRF token from meta tag
const getCSRFToken = () => {
  const token = document.querySelector('meta[name="csrf-token"]')
  return token ? token.getAttribute('content') : null
}

// Create axios instance with default configuration
const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

// Add CSRF token to all requests
api.interceptors.request.use((config) => {
  const token = getCSRFToken()
  if (token) {
    config.headers['X-CSRF-Token'] = token
  }
  return config
})

// Handle errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = '/users/sign_in'
    }
    return Promise.reject(error)
  }
)

// Project Tabs API
export const projectTabsAPI = {
  create: (projectId, name) => api.post(`/projects/${projectId}/tabs.json`, { name }),
  update: (projectId, id, name) => api.patch(`/projects/${projectId}/tabs/${id}.json`, { name }),
  delete: (projectId, id) => api.delete(`/projects/${projectId}/tabs/${id}.json`),
}

// Projects API
export const projectsAPI = {
  list: () => api.get('/projects.json'),
  listArchived: () => api.get('/projects.json?archived=true'),
  get: (id) => api.get(`/projects/${id}.json`),
  create: (data) => api.post('/projects.json', { project: data }),
  update: (id, data) => api.patch(`/projects/${id}.json`, { project: data }),
  delete: (id) => api.delete(`/projects/${id}.json`),
  archive: (id) => api.patch(`/projects/${id}/archive.json`),
  unarchive: (id) => api.patch(`/projects/${id}/unarchive.json`),
}

// Project Work Stages API
export const workStagesAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/work_stages.json`),
  create: (projectId, data) => api.post(`/projects/${projectId}/work_stages.json`, data),
  update: (projectId, id, data) => api.patch(`/projects/${projectId}/work_stages/${id}.json`, data),
  delete: (projectId, id) => api.delete(`/projects/${projectId}/work_stages/${id}.json`),
  reorder: (projectId, ids) => api.patch(`/projects/${projectId}/work_stages/reorder.json`, { ids }),
  toggleCompletion: (projectId, id) => api.patch(`/projects/${projectId}/work_stages/${id}/toggle_completion.json`),
}

// Project Stage Works API
export const stageWorksAPI = {
  list: (projectId, stageId) => api.get(`/projects/${projectId}/work_stages/${stageId}/project_stage_works.json`),
  create: (projectId, stageId, data) => api.post(`/projects/${projectId}/work_stages/${stageId}/project_stage_works.json`, { project_stage_work: data }),
  update: (projectId, stageId, id, data) => api.patch(`/projects/${projectId}/work_stages/${stageId}/project_stage_works/${id}.json`, { project_stage_work: data }),
  delete: (projectId, stageId, id) => api.delete(`/projects/${projectId}/work_stages/${stageId}/project_stage_works/${id}.json`),
  reorder: (projectId, stageId, ids) => api.patch(`/projects/${projectId}/work_stages/${stageId}/project_stage_works/reorder.json`, { ids }),
  toggleCompletion: (projectId, stageId, id) => api.patch(`/projects/${projectId}/work_stages/${stageId}/project_stage_works/${id}/toggle_completion.json`),
}

// Stage Tasks API
export const stageTasksAPI = {
  list: (projectId, stageId) => api.get(`/projects/${projectId}/work_stages/${stageId}/tasks.json`),
  create: (projectId, stageId, title) => api.post(`/projects/${projectId}/work_stages/${stageId}/tasks.json`, { stage_task: { title } }),
  delete: (projectId, stageId, id) => api.delete(`/projects/${projectId}/work_stages/${stageId}/tasks/${id}.json`),
  toggleCompletion: (projectId, stageId, id) => api.patch(`/projects/${projectId}/work_stages/${stageId}/tasks/${id}/toggle_completion.json`),
}

// Stage Acts API (папка с готовыми актами, загружаются вручную)
export const stageActsAPI = {
  list: (projectId, stageId) => api.get(`/projects/${projectId}/work_stages/${stageId}/acts.json`),
  create: (projectId, stageId, formData) =>
    api.post(`/projects/${projectId}/work_stages/${stageId}/acts.json`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  delete: (projectId, stageId, id) => api.delete(`/projects/${projectId}/work_stages/${stageId}/acts/${id}.json`),
  togglePaid: (projectId, stageId, id) =>
    api.patch(`/projects/${projectId}/work_stages/${stageId}/acts/${id}/toggle_paid.json`),
}

// Materials API
export const materialsAPI = {
  create: (workId, formData) => {
    return api.post(`/project_stage_works/${workId}/materials.json`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  delete: (workId, id) => api.delete(`/project_stage_works/${workId}/materials/${id}.json`),
}

// ASR Records API
export const asrAPI = {
  create: (workId, formData) => {
    return api.post(`/project_stage_works/${workId}/asr_records.json`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  update: (workId, id, formData) => {
    return api.patch(`/project_stage_works/${workId}/asr_records/${id}.json`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  delete: (workId, id) => api.delete(`/project_stage_works/${workId}/asr_records/${id}.json`),
}

// Work Stages (for selection in modal)
export const workStagesLibraryAPI = {
  list: () => api.get('/work_stages.json'),
}

export default api
